import { unflatten } from 'flat';
import Validator from '../../../../components/forms/validate';
import { ADVISORY_SEVERITIES, MATCH_OPS } from '../../../../constants/firmware-advisories';
import { normalizeVersions } from '../../../../data/firmwareAdvisory/mapper';

/*
 * Every field the API requires on create. PATCH takes them all as optional, but the form always
 * sends the full body (see formValuesToWritePayload), so one rule set serves both.
 *
 * The `in:` guards on the operators and the severity are not defensive theatre: those three fields
 * are populated by the component type pre-fill rather than typed, so a bad value would otherwise
 * reach the API without anyone having looked at it.
 */
const RULES = {
	'componentTypeId': ['required'],
	'manufacturerMatchOp': ['required', `in:${MATCH_OPS.join(',')}`],
	'manufacturerMatchValue': ['required'],
	'modelMatchOp': ['required', `in:${MATCH_OPS.join(',')}`],
	'modelMatchValue': ['required'],
	'affectedVersions': ['required'],
	'targetVersion': ['required'],
	'severity': ['required', `in:${ADVISORY_SEVERITIES.join(',')}`],
	'kbUrl': ['required', 'url'],
	'vendorUrl': ['required', 'url']
};

/*
 * `required` reads the raw value, so it receives the actual array for `affectedVersions` and
 * rejects `[]` - `String([])` is the empty string. It works by stringification rather than by
 * design, which is worth knowing before anyone "fixes" it to `min:1`: that rule sizes arrays
 * correctly but has no array message, so it renders "Must be at least 1 characters".
 */
const CUSTOM_MESSAGES = {
	'required.affectedVersions': 'Select or type at least one affected firmware version',
	'required.componentTypeId': 'Choose a component type',
	'in.severity': 'Choose a severity',
	'url.kbUrl': 'Must be a valid URL',
	'url.vendorUrl': 'Must be a valid URL'
};

export const validateAdvisoryForm = (values) => {
	const validator = new Validator(values, RULES, CUSTOM_MESSAGES);
	const errors = validator.fails() ? unflatten(validator.errors.all()) : {};

	/*
	 * The one rule validatorjs cannot express: an advisory that tells an appliance to upgrade to a
	 * version it also calls affected is self-contradictory, and the API will happily store it.
	 */
	const targetVersion = String(values?.targetVersion ?? '').trim();

	if (!errors.targetVersion && targetVersion && normalizeVersions(values?.affectedVersions).includes(targetVersion)) {
		errors.targetVersion = 'The target version cannot also be an affected version';
	}

	return errors;
};

export default validateAdvisoryForm;
